import { useEffect, useRef } from 'react'
import { AppState, type AppStateStatus } from 'react-native'
import { useAuth } from '@/lib/auth'
import { log } from '@/lib/logger'
import {
    getTokenTTL,
    queryClient,
    reconnectRealtimeIfDead,
    refreshAuthToken,
} from '@/lib/pocketbase'

const CHECK_INTERVAL_MS = 3 * 60_000
const REFRESH_THRESHOLD_SECONDS = 4 * 60 * 60

export function useRealtimeHealthCheck() {
    const { isLoggedIn } = useAuth({ throwIfAnon: false })
    const appStateRef = useRef<AppStateStatus>(AppState.currentState)

    useEffect(() => {
        if (!isLoggedIn) return

        const subscription = AppState.addEventListener('change', async (next: AppStateStatus) => {
            const wasBackground = appStateRef.current.match(/inactive|background/)
            appStateRef.current = next

            if (wasBackground && next === 'active') {
                log.debug('App foregrounded, refreshing data and checking realtime')

                const ttl = getTokenTTL()
                if (ttl < REFRESH_THRESHOLD_SECONDS) {
                    log.info(`Auth token expires in ${Math.round(ttl / 60)}m, refreshing`)
                    try {
                        await refreshAuthToken()
                    } catch {
                        log.warn('Failed to refresh auth token on foreground')
                    }
                }

                reconnectRealtimeIfDead()
                queryClient.invalidateQueries()
            }
        })

        const intervalId = setInterval(reconnectRealtimeIfDead, CHECK_INTERVAL_MS)

        return () => {
            subscription.remove()
            clearInterval(intervalId)
        }
    }, [isLoggedIn])
}
